import { useEffect, useMemo, useState } from 'react'

import { Loader, SegmentedControl, Select, Stack, Text, TextInput } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconHash } from '@tabler/icons-react'
import { useGetList, useTranslate } from 'react-admin'
import { useGetApiVideoPromotionYoutubeVideosVideoId } from '../../../codegen/generated/video-promotion'
import { VideoPromotionHelper } from '../../../helpers/VideoPromotion.helper'
import { useVideoPromotionStore } from '../../../stores/useVideoPromotionStore'
import { YoutubeChannel } from '../../../types/YoutubeChannel'
import { LXVideoSelect } from '../../LXVideoSelect'

type TargetVideo = { id: string; title: string }

type PromotionTargetVideoSelectProps = {
  label?: string
  value: string | undefined
  onChange: (video: TargetVideo | undefined) => void
}

type Mode = 'search' | 'id'

/** Target of an endscreen element or an infocard: a synced video of any channel, or any YouTube video by id/URL */
export const PromotionTargetVideoSelect = ({
  label,
  value,
  onChange,
}: PromotionTargetVideoSelectProps) => {
  const t = useTranslate()
  const operationChannelId = useVideoPromotionStore(state => state.youtubeChannelId)
  // a stored target may belong to any channel: reopen it by id, which resolves its title from YouTube
  const [mode, setMode] = useState<Mode>(value ? 'id' : 'search')
  const [channelId, setChannelId] = useState(operationChannelId)
  const [input, setInput] = useState(value ?? '')
  const [debouncedInput] = useDebouncedValue(input, 400)
  const parsedVideoId = VideoPromotionHelper.parseYoutubeVideoId(debouncedInput)

  const youtubeChannelsQuery = useGetList<YoutubeChannel>('youtubeChannels', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })
  const youtubeChannels = useMemo(
    () => (youtubeChannelsQuery.data ?? []).map(c => ({ value: c.id, label: c.name })),
    [youtubeChannelsQuery.data],
  )

  const lookupQuery = useGetApiVideoPromotionYoutubeVideosVideoId(
    parsedVideoId ?? '',
    { youtubeChannelId: operationChannelId ?? '' },
    { query: { enabled: mode === 'id' && !!parsedVideoId && !!operationChannelId, retry: false } },
  )
  const resolvedVideo =
    parsedVideoId && lookupQuery.data?.body.video.id === parsedVideoId
      ? lookupQuery.data.body.video
      : undefined

  useEffect(() => {
    if (resolvedVideo && resolvedVideo.id !== value) {
      onChange({ id: resolvedVideo.id, title: resolvedVideo.title })
    }
  }, [resolvedVideo, value, onChange])

  const onInputChange = (next: string) => {
    setInput(next)
    if (value && VideoPromotionHelper.parseYoutubeVideoId(next) !== value) {
      onChange(undefined)
    }
  }

  const onModeChange = (next: Mode) => {
    setMode(next)
    setInput('')
    if (value) {
      onChange(undefined)
    }
  }

  return (
    <Stack gap={6} w="100%">
      {label && (
        <Text size="sm" fw={500}>
          {label}
        </Text>
      )}
      <SegmentedControl
        size="xs"
        fullWidth
        value={mode}
        onChange={next => onModeChange(next as Mode)}
        data={[
          { value: 'search', label: t('pages.videoPromotion.targetVideo.search') },
          { value: 'id', label: t('pages.videoPromotion.targetVideo.byId') },
        ]}
      />
      {mode === 'search' ? (
        <>
          <Select
            placeholder={t('_generics.youtubeChannel')}
            data={youtubeChannels}
            value={channelId ?? null}
            onChange={id => {
              setChannelId(id ?? undefined)
              if (value) {
                onChange(undefined)
              }
            }}
            searchable
            autoComplete="off"
            data-form-type="other"
          />
          <LXVideoSelect
            placeholder={t('pages.videoPromotion.videoName')}
            youtubeChannelId={channelId}
            value={value}
            onChange={video => onChange(video && { id: video.id, title: video.title })}
          />
        </>
      ) : (
        <>
          <TextInput
            leftSection={<IconHash size={12} />}
            rightSection={lookupQuery.isFetching ? <Loader size="xs" /> : undefined}
            placeholder={t('pages.videoPromotion.targetVideo.idOrUrl')}
            value={input}
            onChange={event => onInputChange(event.currentTarget.value)}
            error={
              debouncedInput.trim() && !parsedVideoId
                ? t('pages.videoPromotion.targetVideo.invalidId')
                : lookupQuery.isError
                  ? t('pages.videoPromotion.targetVideo.notFound')
                  : undefined
            }
            autoComplete="off"
            data-form-type="other"
          />
          {resolvedVideo && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {resolvedVideo.title} · {resolvedVideo.channelTitle}
            </Text>
          )}
          {resolvedVideo?.privacyStatus && resolvedVideo.privacyStatus !== 'public' && (
            <Text size="xs" c="orange">
              {t('pages.videoPromotion.targetVideo.notPublic', {
                status: resolvedVideo.privacyStatus,
              })}
            </Text>
          )}
        </>
      )}
    </Stack>
  )
}
